import { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Filler,
  Legend,
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  MdAccountBalance,
  MdAcUnit,
  MdAssessment,
  MdAutorenew,
  MdBuild,
  MdCardMembership,
  MdEvent,
  MdEventAvailable,
  MdAttachMoney,
  MdPeople,
  MdPayments,
  MdSubscriptions,
  MdFitnessCenter,
  MdSupportAgent,
  MdPayment,
  MdConfirmationNumber,
  MdLock,
  MdHowToReg,
} from 'react-icons/md';
import PageHeader from '../../components/owner/PageHeader';
import PanelStatCard from '../../components/shared/PanelStatCard';
import { useGymSettings } from '../../context/GymSettingsContext';
import { fetchAdminDashboardOverview } from '../../services/admin/adminDashboardData';
import { fetchBranchNames } from '../../services/gym/catalogApi';
import { RECEPTIONIST_SALE_PAGES } from '../receptionist/sales/salePageConfig';
import '../owner/ownerPages.css';
import './adminDashboard.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip,
  Filler,
  Legend,
);

const CHART_REVENUE = '#f59e0b';
const CHART_ATTENDANCE = '#3b82f6';
const CHART_RENEWALS = '#a855f7';
const CHART_MEMBERS = '#22c55e';
const CHART_INVOICES = '#f472b6';
const CHART_VISITORS = '#06b6d4';
const CHART_INCOME = '#94ff00';
const CHART_EXPENSE = '#ef4444';
const TEXT_SECONDARY = '#94a3b8';
const GRID_COLOR = 'rgba(148, 163, 184, 0.08)';

const emptyRange = { startDate: '', endDate: '', branch: 'all' };

const PIE_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#a855f7', '#06b6d4', '#f472b6', '#94ff00', '#ef4444'];

const saleStatIcons = Object.fromEntries(
  RECEPTIONIST_SALE_PAGES.map((page) => [page.source, page.icon]),
);

const statIcons = {
  members: MdPeople,
  newMembership: MdHowToReg,
  newMembershipAmount: MdAttachMoney,
  registrationFee: MdCardMembership,
  packages: MdCardMembership,
  memberships: MdSubscriptions,
  trainers: MdFitnessCenter,
  receptionists: MdSupportAgent,
  attendance: MdEventAvailable,
  revenue: MdAttachMoney,
  finance: MdAccountBalance,
  payments: MdPayments,
  reports: MdAssessment,
  paymentMethods: MdPayment,
  renewals: MdAutorenew,
  guestPass: MdConfirmationNumber,
  classes: MdEvent,
  equipments: MdBuild,
  lockers: MdLock,
  freeze: MdAcUnit,
  remaining: MdPayments,
  ...saleStatIcons,
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#1A1F2B',
      borderColor: 'rgba(148, 163, 184, 0.14)',
      borderWidth: 1,
      titleColor: '#f1f5f9',
      bodyColor: TEXT_SECONDARY,
    },
  },
  scales: {
    x: {
      ticks: { color: TEXT_SECONDARY },
      grid: { color: GRID_COLOR },
    },
    y: {
      ticks: { color: TEXT_SECONDARY },
      grid: { color: GRID_COLOR },
    },
  },
};

function buildLineDataset(label, data, labels, color) {
  return {
    labels,
    datasets: [
      {
        label,
        data,
        borderColor: color,
        backgroundColor: `${color}24`,
        fill: true,
        tension: 0.35,
        pointBackgroundColor: color,
        pointBorderColor: '#0F1218',
        pointBorderWidth: 2,
        pointRadius: 4,
      },
    ],
  };
}

function buildBarDataset(label, data, labels, color) {
  return {
    labels,
    datasets: [
      {
        label,
        data,
        backgroundColor: `${color}BF`,
        borderColor: color,
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };
}

const currencyChartOptions = {
  ...chartOptions,
  scales: {
    ...chartOptions.scales,
    y: {
      ...chartOptions.scales.y,
      ticks: {
        color: TEXT_SECONDARY,
        callback: (value) => `${(value / 1000).toFixed(0)}k`,
      },
    },
  },
};

const financeChartOptions = {
  ...currencyChartOptions,
  plugins: {
    ...currencyChartOptions.plugins,
    legend: {
      display: true,
      position: 'top',
      align: 'end',
      labels: { color: TEXT_SECONDARY, boxWidth: 12, padding: 16 },
    },
  },
};

function buildPieDataset(labels, values) {
  return {
    labels,
    datasets: [
      {
        data: values,
        backgroundColor: labels.map((_, i) => PIE_COLORS[i % PIE_COLORS.length]),
        borderColor: '#0F1218',
        borderWidth: 2,
      },
    ],
  };
}

function ChartBlock({ title, type, data, options, full }) {
  const ChartComponent = type === 'doughnut' || type === 'pie'
    ? Doughnut
    : type === 'bar'
      ? Bar
      : Line;
  return (
    <div className={`rec-dash-chart-item${full ? ' rec-dash-chart-item-full' : ''}`}>
      <h3 className="rec-dash-chart-label">{title}</h3>
      <div className="rec-dash-chart-container">
        <ChartComponent data={data} options={options} />
      </div>
    </div>
  );
}

function Dashboard() {
  const { gym } = useGymSettings();
  const [stats, setStats] = useState([]);
  const [charts, setCharts] = useState(null);
  const [scopeLabel, setScopeLabel] = useState('All branches');
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [draft, setDraft] = useState(emptyRange);
  const [applied, setApplied] = useState(emptyRange);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const names = await fetchBranchNames();
        if (!cancelled) setBranches(names || []);
      } catch {
        if (!cancelled) setBranches([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setError('');
        setLoading(true);
        const data = await fetchAdminDashboardOverview(applied);
        if (cancelled) return;
        setStats(data.stats);
        setCharts(data.charts);
        setScopeLabel(data.scopeLabel || 'All branches');
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load dashboard');
          setStats([]);
          setCharts(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [applied]);

  const chartBlocks = useMemo(() => {
    if (!charts) return [];
    const pieOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: { color: TEXT_SECONDARY, boxWidth: 12, padding: 12 },
        },
        tooltip: chartOptions.plugins.tooltip,
      },
    };
    const list = [];

    if (charts.categorySales?.labels?.length) {
      list.push({
        title: 'Sales by Category',
        type: 'doughnut',
        data: buildPieDataset(charts.categorySales.labels, charts.categorySales.values),
        options: pieOptions,
      });
    }
    if (charts.dailyCollection?.labels?.length) {
      list.push({
        title: 'Daily Collection',
        type: 'bar',
        data: buildBarDataset('Daily Collection', charts.dailyCollection.values, charts.dailyCollection.labels, CHART_REVENUE),
        options: currencyChartOptions,
      });
    }
    if (charts.salesByDay?.labels?.length) {
      list.push({
        title: 'Sales by Day',
        type: 'bar',
        data: buildBarDataset('Sales', charts.salesByDay.values, charts.salesByDay.labels, CHART_INVOICES),
        options: currencyChartOptions,
      });
    }
    if (charts.membershipVsRenewal?.labels?.length) {
      list.push({
        title: 'New Membership vs Renewal',
        type: 'bar',
        data: buildBarDataset('Amount', charts.membershipVsRenewal.values, charts.membershipVsRenewal.labels, CHART_RENEWALS),
        options: currencyChartOptions,
      });
    }

    const financeChartData = {
      labels: charts.finance.labels,
      datasets: [
        {
          label: 'Income',
          data: charts.finance.income,
          backgroundColor: 'rgba(148, 255, 0, 0.75)',
          borderColor: CHART_INCOME,
          borderWidth: 1,
          borderRadius: 6,
        },
        {
          label: 'Expenses',
          data: charts.finance.expenses,
          backgroundColor: 'rgba(239, 68, 68, 0.65)',
          borderColor: CHART_EXPENSE,
          borderWidth: 1,
          borderRadius: 6,
        },
      ],
    };

    list.push(
      { title: 'Monthly Revenue', type: 'bar', data: buildBarDataset('Monthly Revenue', charts.revenue.values, charts.revenue.labels, CHART_REVENUE), options: currencyChartOptions },
      { title: 'Weekly Attendance', type: 'line', data: buildLineDataset('Daily Attendance', charts.attendance.values, charts.attendance.labels, CHART_ATTENDANCE), options: chartOptions },
      { title: 'Membership Renewals', type: 'line', data: buildLineDataset('Membership Renewals', charts.renewals.values, charts.renewals.labels, CHART_RENEWALS), options: chartOptions },
      { title: 'Daily Invoice Collection', type: 'bar', data: buildBarDataset('Invoice Collection', charts.invoices.values, charts.invoices.labels, CHART_INVOICES), options: currencyChartOptions },
      { title: 'Visitor Trend', type: 'line', data: buildLineDataset('Daily Visitors', charts.visitors.values, charts.visitors.labels, CHART_VISITORS), options: chartOptions },
      { title: 'Income vs Expenses', type: 'bar', data: financeChartData, options: financeChartOptions, full: true },
      { title: 'Member Registrations', type: 'line', data: buildLineDataset('New Registrations', charts.registrations.values, charts.registrations.labels, CHART_MEMBERS), options: chartOptions, full: true },
    );
    return list;
  }, [charts]);

  const gridCharts = chartBlocks.filter((c) => !c.full);
  const fullCharts = chartBlocks.filter((c) => c.full);
  const rangeActive = Boolean(applied.startDate || applied.endDate || (applied.branch && applied.branch !== 'all'));

  const handleSearch = (e) => {
    e.preventDefault();
    let { startDate, endDate, branch } = draft;
    if (startDate && endDate && endDate < startDate) endDate = startDate;
    setApplied({
      startDate: startDate || '',
      endDate: endDate || '',
      branch: branch || 'all',
    });
  };

  const handleClear = () => {
    setDraft(emptyRange);
    setApplied(emptyRange);
  };

  return (
    <div>
      <PageHeader
        title="Dashboard"
        subtitle={
          String(gym?.name || '').trim()
            ? `${String(gym.name).trim()} — members, finance, attendance, and gym operations`
            : 'Overview of members, finance, attendance, and gym operations'
        }
      />

      <form className="panel-dash-filters" onSubmit={handleSearch}>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="admin-dash-branch">Branch</label>
          <select
            id="admin-dash-branch"
            className="owner-form-input"
            value={draft.branch || 'all'}
            onChange={(e) => setDraft((d) => ({ ...d, branch: e.target.value }))}
          >
            <option value="all">All branches</option>
            {branches.map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="admin-dash-start">Start date</label>
          <input
            id="admin-dash-start"
            type="date"
            className="owner-form-input"
            value={draft.startDate}
            max={draft.endDate || undefined}
            onChange={(e) => setDraft((d) => ({ ...d, startDate: e.target.value }))}
          />
        </div>
        <div className="owner-form-group">
          <label className="owner-form-label" htmlFor="admin-dash-end">End date</label>
          <input
            id="admin-dash-end"
            type="date"
            className="owner-form-input"
            value={draft.endDate}
            min={draft.startDate || undefined}
            onChange={(e) => setDraft((d) => ({ ...d, endDate: e.target.value }))}
          />
        </div>
        <div className="panel-dash-filter-actions">
          <button type="submit" className="owner-btn owner-btn-primary owner-btn-sm">Search</button>
          <button type="button" className="owner-btn owner-btn-secondary owner-btn-sm" onClick={handleClear}>Clear</button>
        </div>
      </form>

      {rangeActive && (
        <p className="owner-placeholder-text panel-dash-applied">
          Showing: {scopeLabel}
          {(applied.startDate || applied.endDate)
            ? ` · ${applied.startDate || '…'} → ${applied.endDate || '…'}`
            : ''}
        </p>
      )}
      {!rangeActive && (
        <p className="owner-placeholder-text panel-dash-applied">
          Scope: {scopeLabel} (pick a branch to match Reception totals)
        </p>
      )}

      {loading && <p className="owner-placeholder-text owner-mb-16">Loading dashboard...</p>}
      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}

      <div className="admin-dash-stat-grid rec-panel-stat-grid rec-panel-stat-grid-4">
        {stats.map((stat) => {
          const Icon = statIcons[stat.key];
          return (
            <PanelStatCard
              key={stat.key}
              statKey={stat.key}
              label={stat.label}
              value={stat.value}
              change={stat.change}
              variant={stat.variant}
              icon={Icon}
            />
          );
        })}
      </div>

      {charts && (
        <div className="owner-card owner-mb-28 rec-dash-charts-card rec-panel-card">
          <h2 className="owner-card-title rec-dash-charts-heading">Charts & Analytics</h2>
          <div className="rec-dash-charts-body">
            <div className="rec-dash-charts-grid rec-dash-charts-grid-extended">
              {gridCharts.map((chart) => (
                <ChartBlock key={chart.title} {...chart} />
              ))}
            </div>
            {fullCharts.map((chart) => (
              <ChartBlock key={chart.title} {...chart} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
